import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The reads for the MCP Gateway screen (task 7.2; design §10, requirement 13.2).
 *
 * Four operator-API operations back the screen, every one role `viewer`, taken straight from the
 * generated `@oathera/contract/api` and never redefined here (the handlers were wired in task 4.6):
 *
 * - `GET /api/v1/mcp/tools?from=…&to=…` → {@link McpToolPage}: one page of forwarded tools, each with
 *   forwarded `calls`, the zero-filled outcome mix (the `tool_outcome` members plus the folded
 *   allow/deny boundary decisions), handler latency percentiles and error rate. Feeds the tiles and
 *   the tools table.
 * - `GET /api/v1/mcp/tools/{tool}?from=…&to=…` → {@link McpToolDetail}: the per-tool drawer — the
 *   same summary, outcomes-over-time, top agents and a page of recent decisions linked to their
 *   trace and event. Only fetched while a tool is selected (`enabled`), so closing the drawer stops
 *   the query.
 * - `GET /api/v1/mcp/tool-surface?from=…&to=…` → {@link McpToolSurfacePage}: the current inventory
 *   per upstream with the enforced flag and the unenforced total. Feeds the Tool-surface tab.
 * - `GET /api/v1/mcp/tool-surface/drift?from=…&to=…` → {@link McpToolSurfaceDriftPage}: the drift
 *   timeline per `(upstream_id, tool)`, newest first, with `critical` and `recorded` flags.
 *
 * Every window is resolved from the shared time-range exactly as the Decisions/Enforcement screens
 * do, so pausing freezes every query: in live mode "now" is bucketed to a 10s grid to keep the
 * query key stable between ticks; when frozen (paused or custom) `resolveWindow` returns the exact
 * frozen window and the keys stop advancing (design 12.2, FR-13 AC2). The summaries are REST
 * snapshots (aggregates over the whole window), not live-stream views. Fetches go through
 * `lib/api.ts`, so a cold-core 503 surfaces as a `waking` {@link ApiError} and the shell shows
 * "Waking the control plane"; queries are retried per `query-client.ts`.
 */

/** The contract page of per-tool summaries (tiles + tools table). */
export type McpToolPage = components["schemas"]["McpToolPage"];
/** One per-tool summary row (calls, outcome mix, latency percentiles, error rate). */
export type McpToolSummary = components["schemas"]["McpToolSummary"];
/** One category of a tool's outcome mix (a `tool_outcome` member or a folded boundary decision). */
export type ToolOutcomeMixEntry = components["schemas"]["ToolOutcomeMixEntry"];
/** The per-tool drawer detail (summary, outcomes over time, agents, recent decisions). */
export type McpToolDetail = components["schemas"]["McpToolDetail"];
/** One agent's use of a tool (calls + first use). */
export type McpToolAgentUse = components["schemas"]["McpToolAgentUse"];
/** A recent decision behind a tool call, linked to its trace and event. */
export type McpToolDecisionRef = components["schemas"]["McpToolDecisionRef"];
/** The tool-surface inventory page (per upstream, enforced flag, unenforced total). */
export type McpToolSurfacePage = components["schemas"]["McpToolSurfacePage"];
/** One surfaced tool (upstream, tool, enforced, default-deny, hashes, first/last seen). */
export type McpSurfaceTool = components["schemas"]["McpSurfaceTool"];
/** The drift timeline page. */
export type McpToolSurfaceDriftPage = components["schemas"]["McpToolSurfaceDriftPage"];
/** One drift entry (change kind, critical/recorded flags, timestamp). */
export type McpToolSurfaceDriftEntry = components["schemas"]["McpToolSurfaceDriftEntry"];
/** A number that says whether it is exact or an estimate. */
export type Quantity = components["schemas"]["Quantity"];
/** The Gateway access page: observations plus the zero-filled `passport_state` summary (contract 1.7). */
export type McpGatewayAccessPage = components["schemas"]["McpGatewayAccessPage"];
/** One `gateway_access_observation` (bounded identifiers and enums only). */
export type McpGatewayAccessItem = components["schemas"]["McpGatewayAccessItem"];
/** One `passport_state` count of the summary. */
export type McpGatewayPassportStateCount = components["schemas"]["McpGatewayPassportStateCount"];
/** The generated `passport_state` enum (`ok`, `warn`, `unavailable`, `pending`, `other`). */
export type PassportState = components["schemas"]["enum_passport_state"];
/** The generated `gateway_access_outcome` enum. */
export type GatewayAccessOutcome = components["schemas"]["enum_gateway_access_outcome"];
/** The generated `gateway_source_kind` enum. */
export type GatewaySourceKind = components["schemas"]["enum_gateway_source_kind"];
/** The generated `gateway_mode` enum. */
export type GatewayMode = components["schemas"]["enum_gateway_mode"];

/** The resolved time window a query ran for, so the screen can label the snapshot honestly. */
export interface McpWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

/** Resolve the current window from the time-range (live buckets to 10s; frozen is exact). */
function useResolvedWindow(): McpWindow {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  return { fromMs: resolved.fromMs, toMs: resolved.toMs };
}

export interface UseMcpToolsResult {
  readonly query: UseQueryResult<McpToolPage, ApiError>;
  readonly window: McpWindow;
}

/** Fetch one page of per-tool summaries (tiles + tools table) for the current window. */
export function useMcpTools(): UseMcpToolsResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<McpToolPage, ApiError>({
    queryKey: ["mcp-tools", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<McpToolPage>("/mcp/tools", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * Fetch the per-tool drawer detail for `tool`. The query is `enabled` only when a tool is selected,
 * so closing the drawer (`tool === null`) stops the query; the key carries the tool and the window
 * so switching tools or windows refetches and the drawer never shows a stale tool's figures.
 */
export function useMcpToolDetail(tool: string | null): UseQueryResult<McpToolDetail, ApiError> {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  return useQuery<McpToolDetail, ApiError>({
    queryKey: ["mcp-tool", tool, window.fromMs, window.toMs],
    enabled: tool !== null,
    queryFn: ({ signal }) =>
      apiFetch<McpToolDetail>(`/mcp/tools/${encodeURIComponent(tool as string)}`, {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
  });
}

export interface UseMcpToolSurfaceResult {
  readonly query: UseQueryResult<McpToolSurfacePage, ApiError>;
  readonly window: McpWindow;
}

/** Fetch the current tool-surface inventory per upstream for the current window. */
export function useMcpToolSurface(): UseMcpToolSurfaceResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<McpToolSurfacePage, ApiError>({
    queryKey: ["mcp-tool-surface", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<McpToolSurfacePage>("/mcp/tool-surface", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/** Fetch the tool-surface drift timeline (newest first) for the current window. */
export function useMcpToolSurfaceDrift(): UseQueryResult<McpToolSurfaceDriftPage, ApiError> {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  return useQuery<McpToolSurfaceDriftPage, ApiError>({
    queryKey: ["mcp-tool-surface-drift", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<McpToolSurfaceDriftPage>("/mcp/tool-surface/drift", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch the Gateway access observations and Passport-state summary for the current window
 * (`GET /api/v1/mcp/gateway-access`, contract 1.7). `passportState` is the optional generated-type
 * filter; it narrows the items only, the server's summary always counts every observation. The
 * query runs only while `enabled` (the Access tab is on screen).
 */
export function useMcpGatewayAccess(
  passportState: PassportState | null,
  enabled: boolean,
): UseQueryResult<McpGatewayAccessPage, ApiError> {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  return useQuery<McpGatewayAccessPage, ApiError>({
    queryKey: ["mcp-gateway-access", passportState, window.fromMs, window.toMs],
    enabled,
    queryFn: ({ signal }) =>
      apiFetch<McpGatewayAccessPage>("/mcp/gateway-access", {
        query: {
          from: window.fromMs,
          to: window.toMs,
          ...(passportState !== null ? { passport_state: passportState } : {}),
        },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    placeholderData: keepPreviousData,
  });
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
